import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { dossierAPI, authAPI } from '@/utils/api';
import { toast } from 'sonner';
import Header from '@/components/Header';

const UserDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const [userResponse, dossiersResponse] = await Promise.all([
        authAPI.getMe(),
        dossierAPI.getUserDossiers()
      ]);
      setUser(userResponse.data);
      setDossiers(dossiersResponse.data);
    } catch (error) {
      console.error('Fetch error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expirée, veuillez vous reconnecter');
        handleLogout();
      } else {
        toast.error('Erreur lors du chargement des données');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const getScoreColor = (score) => {
    if (score <= 30) return 'text-red-600';
    if (score <= 70) return 'text-orange-600';
    return 'text-green-600';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f8fafc' }}>
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f8fafc' }}>
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h2 className="text-3xl font-bold" style={{ color: '#1e3a8a' }}>
            Bonjour {user?.nom}
          </h2>
          <p className="text-gray-600 mt-2">Bienvenue sur votre tableau de bord</p>
        </div>

        <div className="card p-6 mb-8">
          <h3 className="text-xl font-semibold mb-4" style={{ color: '#1e3a8a' }}>
            Mes informations
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="font-medium text-gray-800">{user?.email}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Téléphone</p>
              <p className="font-medium text-gray-800">{user?.telephone}</p>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-2xl font-bold" style={{ color: '#1e3a8a' }}>
              Mes dossiers ({dossiers.length})
            </h3>
            <Button
              data-testid="new-dossier-button"
              onClick={() => navigate('/')}
              style={{ backgroundColor: '#1e3a8a', color: 'white' }}
            >
              Nouveau dossier
            </Button>
          </div>
        </div>

        {dossiers.length === 0 ? (
          <div className="card p-12 text-center">
            <FileText size={64} color="#9ca3af" className="mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Aucun dossier
            </h3>
            <p className="text-gray-600 mb-6">
              Vous n'avez pas encore soumis de dossier d'analyse.
            </p>
            <Button
              data-testid="start-analysis-button"
              onClick={() => navigate('/')}
              style={{ backgroundColor: '#1e3a8a', color: 'white' }}
            >
              Démarrer une analyse
            </Button>
          </div>
        ) : (
          <div className="grid gap-6">
            {dossiers.map((dossier) => (
              <div
                key={dossier.id}
                data-testid={`dossier-card-${dossier.id}`}
                className="card p-6 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate(`/score/${dossier.id}`)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4 mb-3">
                      <FileText size={24} color="#1e3a8a" />
                      <div>
                        <h4 className="font-semibold text-gray-800">
                          Dossier #{dossier.id.slice(0, 8)}
                        </h4>
                        <div className="flex items-center text-sm text-gray-500 mt-1">
                          <Calendar size={14} className="mr-1" />
                          {formatDate(dossier.created_at)}
                        </div>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4 mt-4">
                      <div>
                        <p className="text-sm text-gray-500">Score</p>
                        {dossier.score !== null ? (
                          <p className={`text-2xl font-bold ${getScoreColor(dossier.score)}`}>
                            {dossier.score}/100
                          </p>
                        ) : (
                          <p className="text-sm text-gray-400">En cours d'analyse...</p>
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Fichiers</p>
                        <p className="font-medium text-gray-800">
                          {dossier.files?.length || 0} document(s)
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Statut</p>
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                            dossier.payment_status === 'paid'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {dossier.payment_status === 'paid' ? 'Expert activé' : 'En attente'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDashboard;